import {
  createFileRoute,
  Link,
  Outlet,
  useChildMatches,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { FileSearch, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth } from "@/lib/auth-client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TransactionStatusBadge } from "@/components/transaction-status-badge";
import { RFQ_STATUS_CONFIG } from "@/lib/status-config";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/page-header";
import { rowActivation } from "@/lib/row-navigation";
import { formatDate, formatDateTime } from "@/lib/format";
import type { RFQListItemDto, VendorDto } from "@procureflow/shared-types";

// US-053 — RFQ list view.
//
// Replaces the Lovable card-grid stub with a shadcn Table list per the
// PR / PO precedent. fetchWithAuth handles cookies + mswReady at the
// transport layer (apps/web/src/lib/auth-client.ts:240-266), so the
// MSW mount-time fetch pattern (CLAUDE.md) is satisfied without an
// explicit `await mswReady` in this component — the import statement
// for fetchWithAuth is the canonical entry point that already awaits it.
//
// The component re-fetches whenever status / vendorId / search change,
// so the same useEffect drives both initial mount + filter updates.
export const Route = createFileRoute("/rfq")({
  head: () => ({
    meta: [
      { title: "RFQ Management — ProcureFlow" },
      {
        name: "description",
        content: "Create and manage Requests for Quotation, send to vendors and track responses.",
      },
    ],
  }),
  component: function RFQPageOrOutlet() {
    // PR/vendors precedent: when a child route (e.g. /rfq/$id) is
    // active, render its outlet; otherwise render the list. Without
    // this, /rfq/$id would silently render the list page because the
    // file `rfq.tsx` is the parent layout in TanStack's file-based
    // routing tree.
    const child = useChildMatches();
    return child.length > 0 ? <Outlet /> : <RFQListPage />;
  },
});

type RfqStatus = "All" | "Open" | "Distributed" | "Closed";

const STATUS_TABS: RfqStatus[] = ["All", "Open", "Distributed", "Closed"];

function RFQListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<RfqStatus>("All");
  const [vendorId, setVendorId] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [rfqs, setRfqs] = useState<RFQListItemDto[]>([]);
  const [vendors, setVendors] = useState<VendorDto[]>([]);
  const [loading, setLoading] = useState(true);

  // RBAC guard — only ProcurementOfficer + Admin may view (CONTEXT.md
  // §"RFQ read auth"). Finance + Requester see the forbidden message
  // instead of the list. The MSW handler also enforces this server-side
  // (apps/web/src/mocks/handlers/rfqs.ts:325 + apps/api/src/rfqs/
  // rfqs.controller.ts:47 @Roles), so this guard is just the UX cap.
  const allowed = user?.role === "ProcurementOfficer" || user?.role === "Admin";

  // Debounce the search input — 250ms keeps the URL tidy and avoids
  // hammering the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch the RFQ list on mount and whenever a filter changes.
  // fetchWithAuth awaits mswReady internally (auth-client.ts:246-249),
  // so the MSW mount-time fetch pattern is satisfied here.
  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (status !== "All") params.set("status", status);
        if (vendorId) params.set("vendor_id", vendorId);
        if (debouncedSearch) params.set("rfq_number", debouncedSearch);
        const qs = params.toString();
        const url = qs ? `/api/rfqs?${qs}` : "/api/rfqs";
        const res = await fetchWithAuth(url);
        if (!res.ok) {
          if (!cancelled) {
            setRfqs([]);
            setLoading(false);
          }
          return;
        }
        const json = (await res.json()) as {
          data: RFQListItemDto[];
          meta: { total: number; page: number; limit: number };
        };
        if (!cancelled) {
          setRfqs(json.data ?? []);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setRfqs([]);
          setLoading(false);
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [allowed, status, vendorId, debouncedSearch]);

  // Fetch the active-vendor list for the filter dropdown. Runs once
  // per role-flip; the list rarely changes during a session.
  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth("/api/vendors?status=Active");
        if (!res.ok) return;
        const json = (await res.json()) as { data: VendorDto[] };
        if (!cancelled) setVendors(json.data ?? []);
      } catch {
        // Swallow — vendor filter is a non-critical enhancement; the
        // list still renders without it.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  if (!allowed) {
    return (
      <div className="space-y-6">
        <PageHeader description="Manage RFQs end-to-end and collect competitive quotations." />
        <div className="rounded-2xl bg-card border border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">You don&apos;t have access to RFQs.</p>
        </div>
      </div>
    );
  }

  // US-063 — Create RFQ from one or more PRs. The builder POSTs
  // /api/rfqs/from-prs which is ProcurementOfficer-only; gate the CTA to match.
  const canCreateRfq = user?.role === "ProcurementOfficer";

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader description="Manage RFQs end-to-end and collect competitive quotations." />
        {canCreateRfq && (
          <Button asChild data-testid="create-rfq-button">
            <Link to="/rfq/build">
              <Plus className="h-3.5 w-3.5" /> Create RFQ
            </Link>
          </Button>
        )}
      </div>

      {/* Status tabs — rendered as buttons (mirrors PR / PO precedent) */}
      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by status">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setStatus(tab)}
            className={
              tab === status
                ? "rounded-lg bg-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground"
                : "rounded-lg border border-border px-3.5 py-1.5 text-sm hover:bg-accent"
            }
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Filter bar — rfq_number search + vendor select */}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="search"
          placeholder="Search RFQ number"
          aria-label="RFQ number search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Select
          value={vendorId || "__all__"}
          onValueChange={(v) => setVendorId(v === "__all__" ? "" : v)}
        >
          <SelectTrigger className="max-w-xs" aria-label="Filter by vendor">
            <SelectValue placeholder="Filter by vendor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All vendors</SelectItem>
            {vendors.map((v) => (
              <SelectItem key={v.id} value={v.id}>
                {v.company_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-card border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>RFQ Number</TableHead>
              <TableHead>Source PR</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Deadline</TableHead>
              <TableHead>Vendors</TableHead>
              <TableHead>Quotes</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : rfqs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                  No RFQs found
                </TableCell>
              </TableRow>
            ) : (
              rfqs.map((rfq) => (
                <TableRow
                  key={rfq.id}
                  className="cursor-pointer hover:bg-muted/30 transition-colors"
                  {...rowActivation(() => navigate({ to: "/rfq/$id", params: { id: rfq.id } }))}
                >
                  <TableCell>
                    {/* Plain anchor — TanStack's typed Link can't yet
                        reference /rfq/$id because that route is owned by
                        Plan 04-03 and not in routeTree.gen.ts at this
                        moment. The href shape /rfq/<uuid> matches the
                        target route the sibling plan adds. */}
                    <a href={`/rfq/${rfq.id}`} className="flex items-center gap-3 group">
                      <div className="h-8 w-8 rounded-lg bg-primary/10 grid place-items-center text-primary shrink-0">
                        <FileSearch className="h-3.5 w-3.5" />
                      </div>
                      <span className="font-mono text-xs font-medium group-hover:text-primary transition-colors">
                        {rfq.rfq_number}
                      </span>
                    </a>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {/* US-063 — show the distinct source PR number(s); fall back
                        to the single pr_number for legacy single-PR RFQs. */}
                    {rfq.source_pr_numbers && rfq.source_pr_numbers.length > 0
                      ? rfq.source_pr_numbers.join(", ")
                      : rfq.pr_number}
                  </TableCell>
                  <TableCell>
                    <TransactionStatusBadge config={RFQ_STATUS_CONFIG[rfq.status]} />
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDateTime(rfq.submission_deadline)}
                  </TableCell>
                  <TableCell className="tabular-nums">{rfq.vendor_count}</TableCell>
                  <TableCell className="tabular-nums">{rfq.quote_count}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDate(rfq.created_at)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
